import axios from "axios"
import React, { useState } from "react"

import { useDispatch, useSelector } from "react-redux"
import { setAlertFunc } from "../../../../../../components/AlertToaster/AlertSlice"
import CircularLoader from "../../../../../../components/Loader/CircularLoader"
import { FileInfo } from "../../../../../../utils/utils"
import { axiosInstance } from "../../../../../Auth/components/axiosInterceptors"

import { useUploadDocumentsMutation } from "pages/Common/fileUploadService"
import { TStore } from "store/Store"
import styles from "./FileUploader.module.scss"

type FileUploaderProps = {
    title: string
    onUpload?: (files: FileInfo[]) => void
    onChange?: (files: FileList) => void
    multiple?: boolean
    accept?: string
    total?: number
    uploadedCount?: number
    error?: boolean
    showError?: boolean
    disabled?: boolean
    ticket_id?: string
    name?: string
    size?: "md" | "lg"
    imageTooltip?: boolean
    containerClass?: string
    hideTooltip?: boolean
    helperText?: string
    is_sub_ticket?: boolean
    onUploading?: (uploading: boolean) => void
}

type Url = {
    file_url: string[] | string
    presigned_url: string
    doc_id?: number
    report_id?: number | null
}

const FileUploader = ({
    title = "Choose Files",
    onUpload = () => {},
    onChange = () => {},
    accept,
    total = 1,
    uploadedCount = 0,
    multiple = false,
    error,
    showError,
    disabled,
    ticket_id,
    name,
    size = "md",
    imageTooltip = false,
    containerClass = "",
    hideTooltip = false,
    helperText = "",
    is_sub_ticket = false,
    onUploading,
}: FileUploaderProps) => {
    const dispatch = useDispatch()
    const userId = useSelector((state: TStore) => state.verifyLogin?.loginData?.user_data?.id)
    const [showTootip, setShowTooltip] = useState(false)
    const [loading, setLoading] = useState(false)
    const [uploadDocuments] = useUploadDocumentsMutation()

    const uploadHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        let fileList = Array.from(event.target.files as FileList)
        if (total) {
            if (event.target.files && event.target.files.length <= total) {
                onChange(event.target.files)
                if (ticket_id) uploadFileToS3(fileList, ticket_id as string)
            } else {
                dispatch(setAlertFunc({ type: "error", msg: `Maximum ${total} files can be selected` } as any))
            }
        } else {
            onChange(event.target.files as FileList)
        }
    }

    const uploadFileToS3 = async (fileArr: File[], ticket_id: string) => {
        setLoading(true)
        onUploading?.(true)
        try {
            const getSafeFileName = (file: File) => {
                const rawName = file.name || "upload"
                const hasExt = rawName.includes(".")
                const safeName = rawName.replace(/[\s-]+/g, "_")
                if (hasExt) return safeName
                const ext = file.type?.split("/")?.[1]?.replace("quicktime", "mov") || "bin"
                return `${safeName}.${ext}`
            }

            const filePayload = [
                {
                    file_type: name ?? "",
                    file_name: is_sub_ticket
                        ? fileArr?.map(file => getSafeFileName(file))
                        : fileArr?.map(file => file.name),
                },
            ]

            const payload = {
                ticket_id: parseInt(ticket_id),
                files: filePayload,
                user_id: userId,
                is_sub_ticket: is_sub_ticket,
            }

            let urls: Url[] | undefined
            if (is_sub_ticket) {
                const response = await uploadDocuments(payload)
                if ("data" in response) {
                    urls = response.data?.urls[0]?.urls
                }
            } else {
                const response = await axiosInstance.post("upload_documents", payload)
                urls = response.data?.urls[0]?.urls
            }
            if (urls && urls?.length > 0) {
                if (typeof urls === "object" && urls.length > 0) {
                    await Promise.all(
                        urls.map(async (url: Record<string, any>, index: number) => {
                            await axios.put(url?.presigned_url, fileArr[index], {
                                headers: {
                                    "Content-Type": fileArr[index]?.type,
                                },
                            })
                        })
                    )
                    let uploadedFilesObj = urls.map(url => ({
                        file_url: is_sub_ticket
                            ? Array.isArray(url.file_url)
                                ? url.file_url[0]
                                : url.file_url
                            : url.file_url[0],
                        doc_id: url.doc_id,
                        report_id: url.report_id ?? undefined,
                    }))
                    onUpload(uploadedFilesObj)
                }
            } else {
                console.error("No URLs returned from the server")
            }
        } catch (error) {
            console.error("Error uploading files:", error)
            dispatch(setAlertFunc({ type: "error", msg: `Error Uploading Files` }))
        } finally {
            setLoading(false)
            onUploading?.(false)
        }
    }

    return (
        <div
            onMouseEnter={() => {
                setShowTooltip(true)
            }}
            onMouseLeave={() => {
                setShowTooltip(false)
            }}
            className={`${styles.download_excel} ${styles[size]} ${disabled ? styles.disabled : ""} ${containerClass}`}
            style={{
                border:
                    showError && error && !disabled
                        ? "1px solid red"
                        : `1px solid ${uploadedCount ? "#101941" : "#70758D"}`,
                cursor: disabled ? "not-allowed" : "pointer",
            }}>
            <span
                className={styles.file_uploader_title}
                style={{ color: uploadedCount && !disabled ? "#FC5735" : "#70758D" }}>
                {loading ? <CircularLoader hide={false} /> : title}
            </span>
            <input
                style={{ height: "36px", opacity: "0", position: "absolute", width: "100%" }}
                accept={accept}
                onChange={e => uploadHandler(e)}
                multiple={multiple}
                type="file"
                title=""
                disabled={disabled}
            />

            {!hideTooltip && showTootip && !disabled && (
                <span
                    style={{
                        fontSize: "10px",
                        color: "#FFFF",
                        padding: "3px",
                        position: "absolute",
                        top: "-20px",
                        left: "0%",
                        backgroundColor: "black",
                    }}
                    className={`${styles.editProfile_modal__form__flex__description__text}`}>
                    {imageTooltip ? `You can select upto ${total} images` : `You can select upto ${total} files`}
                </span>
            )}
            {!!helperText && !uploadedCount && (
                <div
                    style={{
                        fontSize: "10px",
                        padding: "3px",
                        position: "absolute",
                        bottom: "-20px",
                        right: "0%",
                        color: "rgb(112, 117, 141)",
                    }}>
                    {helperText}
                </div>
            )}
            {uploadedCount && !disabled ? (
                <span
                    style={{
                        fontSize: "10px",
                        padding: "3px",
                        position: "absolute",
                        bottom: "-20px",
                        right: "0%",
                        color: "#FC5735",
                    }}>
                    {uploadedCount} File uploaded
                </span>
            ) : null}
        </div>
    )
}

export default FileUploader
